import moment from 'moment';
import { OrderPart, OrderPartDTO } from '../../interfaces/Order';
import { WorkflowCalculation } from '../../interfaces/Workflow';

// Flattens the `package` children of each order part into a single list (preserves the element type).
export const getPackageFromOrderParts = <T extends OrderPart>(orderParts: T[]): T[] => {
  const packageParts: T[] = [];
  orderParts.forEach((part) => {
    if (part.package && part.package.length) {
      packageParts.push(...part.package);
    }
  });
  return packageParts;
};

// Formats a delivery date, dropping a trailing midnight time component.
export const formatDeliveryDate = (date: string | null, longDateFormat: string): string => {
  if (!date) return '';
  return moment(date).format(longDateFormat).replace(/ 00:00(:00)?$/, '');
};

export const resetOrderPartOnCodeChange = (
  part: OrderPartDTO,
  byOeCode: boolean
): OrderPartDTO => ({
  ...part,
  toBeUpdated: true,
  alternativePartQualityId: null,
  originalPrice: 0,
  producer: null,
  deliveryTime: null,
  validityTime: null,
  codeType: null,
  excludeFromOrder: null,
  orderCode: null,
  type: null,
  locationId: null,
  availableStock: null,
  location: null,
  remarks: null,
  alternativePartCode: null,
  alternativePrice: 0,
  alternativePartStockQuantity: null,
  alternativePartStockLocation: null,
  alternativePartRemarks: null,
  productId: null,
  codEan: null,
  bundleItem: null,
  bundleNo: null,
  bundleQuantity: null,
  availableQuantity: null,
  supplierId: null,
  hasBeenVerifiedBySupplier: true,
  byOeCode
});

export const applyOrderPartCodeChange = (
  orderpartsList: OrderPartDTO[],
  partId: number,
  code: string,
  byOeCode: boolean
): OrderPartDTO[] => {
  const orderParts = [...orderpartsList];
  const index = orderParts.findIndex((item) => item.id === partId);
  if (index === -1) return orderParts;

  const reset = resetOrderPartOnCodeChange(orderParts[index], byOeCode);
  orderParts[index] = byOeCode
    ? { ...reset, guideNumber: code, alternativeGuideNumber: null }
    : { ...reset, alternativeGuideNumber: code };

  const current = orderParts[index];
  if (current.groupedUniqueId !== null) {
    for (let i = 0; i < orderParts.length; i++) {
      if (
        current.groupedUniqueId === orderParts[i].groupedUniqueId &&
        current.id !== orderParts[i].id
      ) {
        orderParts[i].isDeleted = true;
        orderParts[i].toBeDeleted = true;
        orderParts[i].hasBeenVerifiedBySupplier = true;
      }
    }
  }
  return orderParts;
};

// Single-part order edit: only alternative selection + quantity are enabled, everything else off.
export const ORDER_PART_WORKFLOW_CALCULATION = {
  selectAlternativePart: true,
  editQuantity: true,
  editGuideNumber: false,
  addOriginalPart: false,
  addAlternativePart: false,
  addNotifySupportOriginalPart: false,
  deleteOriginalPart: false,
  alternativeNetPrice: false,
  hasAlternativePartDescription: false,
  serviceOwnStock: false,
  oilMapping: false
} as WorkflowCalculation;

export const initOrderPartDTO = (op: OrderPart): OrderPartDTO =>
  ({
    ...op,
    prevVal: null,
    hasEmptyGuideNumber: false,
    hasEmptyAlternativeGuideNumber: false,
    alternativeParts: [],
    hasBeenInterogated: false,
    caseCalculationHistoryId: 0,
    calculationSectionId: 1,
    calculationSection: null,
    amount: 0,
    solutionValueId: null,
    solutionValue: null,
    solutionComment: '',
    additionValue: 0,
    additionFixedValue: 0,
    alternativeNetPrice: null,
    additionInsurer: null,
    totalAmount: 0,
    isOwnStock: false,
    auxiliarInformation: null,
    editOriginalPrice: false,
    isNew: false,
    isInStock: false,
    supportComment: null,
    caseCalculationPartInterogations: [],
    manualGTModified: false,
    serviceCalculationPartId: null,
    suggestedVariantsJson: null,
    isServiceRecommendationChanged: false,
    isLoading: false,
    isExpanded: false,
    unitsError: false,
    priceError: false,
    descriptionError: false
  } as OrderPartDTO);
